// src/pages/Blog/BlogListCard.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';

import styled from '@emotion/styled';

import RightArrowButtonIcon from '../../../components/Icons/RightArrowButtonIcon';
import { Post } from '../BlogData';

const defaultCover = 'https://locumspace.co/locumspace-logo.png';

interface BlogListCardProps {
  post: Post;
}

const BlogListCard: React.FC<BlogListCardProps> = ({ post }) => {
  const navigate = useNavigate();

  return (
    <Card>
      <CoverImage
        src={post.coverImageUrl ?? defaultCover}
        alt={post.coverImageUrl ? `${post.title}` : ''}
        onError={(e) => {
          e.currentTarget.src = defaultCover;
          e.currentTarget.alt = `${post.title}`;
        }}
      />
      <Content>
        <Title>{post.title}</Title>
        <Meta>
          By <strong>{post.authorName}</strong> on{' '}
          <time dateTime={post.publishDate}>
            {new Date(post.publishDate).toLocaleDateString()}
          </time>
        </Meta>
        <Excerpt>{post.excerpt}</Excerpt>
        <ReadButton onClick={() => navigate(`/blog/${post.slug}/${post.id}`)}>
          Read More <RightArrowButtonIcon />
        </ReadButton>
      </Content>
    </Card>
  );
};

export default BlogListCard;

const Card = styled.article`
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const CoverImage = styled.img`
  width: 100%;
  height: 160px;
  object-fit: cover;
`;

const Content = styled.div`
  padding: 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h2`
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
`;

const Meta = styled.p`
  margin: 0 0 1rem;
  font-size: 0.875rem;
  color: #666;
`;

const Excerpt = styled.p`
  flex: 1;
  margin: 0 0 1rem;
  line-height: 1.4;
`;

const ReadButton = styled.button`
  align-self: flex-start;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  background: none;
  color: ${({ theme }) => theme.palette.mainBlue};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;

  & svg {
    width: 1.5rem;
  }

  &:hover {
    background: rgb(248, 245, 245);
  }
`;
